import { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import ReplyIcon from '@mui/icons-material/Reply';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { CHAT_REACTIONS } from './chatConstants';
import { buildAbsoluteUrl } from '../../hooks/useEmployeeChat';

const formatSize = bytes => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function MessageBubble({
  message,
  isMine,
  seen,
  onReply,
  onReact,
  onEdit,
  onDelete,
  canModerate,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reactionAnchor, setReactionAnchor] = useState(null);

  const deleted = message.deleted;
  const canDelete = isMine || canModerate;

  const openReactionPicker = e => setReactionAnchor(e.currentTarget);
  const pickReaction = emoji => {
    setReactionAnchor(null);
    onReact(message.id, emoji);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMine ? 'flex-end' : 'flex-start',
        mb: 1,
        '&:hover .msg-actions': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMine ? 'row-reverse' : 'row',
          alignItems: 'center',
          gap: 0.5,
          maxWidth: '78%',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 2,
            borderTopRightRadius: isMine ? 4 : 16,
            borderTopLeftRadius: isMine ? 16 : 4,
            backgroundColor: theme =>
              isMine
                ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.16 : 0.28)
                : theme.palette.mode === 'light'
                  ? theme.palette.grey[100]
                  : 'rgba(255,255,255,0.06)',
            border: theme =>
              `1px solid ${
                theme.palette.mode === 'light' ? theme.palette.divider : 'rgba(255,255,255,0.08)'
              }`,
            minWidth: 60,
          }}
        >
          {/* Quoted reply */}
          {message.replyTo && (
            <Box
              sx={{
                borderLeft: theme => `3px solid ${theme.palette.primary.main}`,
                pl: 1,
                mb: 0.5,
                opacity: 0.8,
              }}
            >
              <Typography variant='caption' sx={{ display: 'block', fontStyle: 'italic' }} noWrap>
                {message.replyTo.preview || 'Nachricht'}
              </Typography>
            </Box>
          )}

          {/* Attachments */}
          {!deleted &&
            (message.attachments || []).map((att, i) =>
              att.kind === 'image' ? (
                <Box
                  key={i}
                  component='a'
                  href={buildAbsoluteUrl(att.url)}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  <Box
                    component='img'
                    src={buildAbsoluteUrl(att.url)}
                    alt={att.name}
                    sx={{
                      maxWidth: 260,
                      maxHeight: 260,
                      width: 'auto',
                      borderRadius: 1.5,
                      display: 'block',
                    }}
                  />
                </Box>
              ) : (
                <Box
                  key={i}
                  component='a'
                  href={buildAbsoluteUrl(att.url)}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                    p: 1,
                    borderRadius: 1.5,
                    textDecoration: 'none',
                    color: 'inherit',
                    maxWidth: 260,
                    border: theme => `1px solid ${theme.palette.divider}`,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <InsertDriveFileOutlinedIcon fontSize='small' />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2' noWrap>
                      {att.name}
                    </Typography>
                    {att.size ? (
                      <Typography variant='caption' color='text.secondary'>
                        {formatSize(att.size)}
                      </Typography>
                    ) : null}
                  </Box>
                  <DownloadIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                </Box>
              )
            )}

          {/* Text / deleted placeholder */}
          {deleted ? (
            <Typography variant='body2' sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
              Nachricht gelöscht
            </Typography>
          ) : (
            message.text && (
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {message.text}
              </Typography>
            )
          )}

          {/* Meta row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              mt: 0.25,
            }}
          >
            {message.edited && !deleted && (
              <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: 10 }}>
                bearbeitet
              </Typography>
            )}
            <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: 10 }}>
              {dayjs(message.createdAt).format('HH:mm')}
            </Typography>
            {isMine && !deleted && (
              <Tooltip title={seen ? 'Gelesen' : 'Gesendet'}>
                {seen ? (
                  <DoneAllIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                ) : (
                  <DoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                )}
              </Tooltip>
            )}
          </Box>
        </Paper>

        {/* Hover actions */}
        {!deleted && (
          <Box className='msg-actions' sx={{ opacity: 0, transition: 'opacity 0.15s', display: 'flex' }}>
            <Tooltip title='Reagieren'>
              <IconButton size='small' onClick={openReactionPicker}>
                <AddReactionOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Antworten'>
              <IconButton size='small' onClick={() => onReply(message)}>
                <ReplyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {(isMine || canDelete) && (
              <IconButton size='small' onClick={e => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      {/* Reactions */}
      {(message.reactions || []).length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
          {message.reactions.map(r => (
            <Box
              key={r.emoji}
              onClick={() => onReact(message.id, r.emoji)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.75,
                py: 0.1,
                borderRadius: 3,
                cursor: 'pointer',
                fontSize: 13,
                lineHeight: 1.6,
                backgroundColor: theme =>
                  r.reactedByMe
                    ? alpha(theme.palette.primary.main, 0.22)
                    : theme.palette.mode === 'light'
                      ? theme.palette.grey[200]
                      : 'rgba(255,255,255,0.08)',
                border: theme =>
                  r.reactedByMe ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
              }}
            >
              <span>{r.emoji}</span>
              {r.count > 1 && (
                <Typography variant='caption' sx={{ fontWeight: 600 }}>
                  {r.count}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Reaction picker popover */}
      <Popover
        open={Boolean(reactionAnchor)}
        anchorEl={reactionAnchor}
        onClose={() => setReactionAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ display: 'flex', gap: 0.5, p: 0.75 }}>
          {CHAT_REACTIONS.map(emoji => (
            <Box
              key={emoji}
              onClick={() => pickReaction(emoji)}
              sx={{
                fontSize: 22,
                cursor: 'pointer',
                px: 0.5,
                borderRadius: 1,
                '&:hover': { transform: 'scale(1.25)' },
                transition: 'transform 0.1s',
              }}
            >
              {emoji}
            </Box>
          ))}
        </Box>
      </Popover>

      {/* More menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {isMine && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onEdit(message);
            }}
          >
            Bearbeiten
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDelete(message.id);
            }}
            sx={{ color: 'error.main' }}
          >
            Löschen
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

MessageBubble.propTypes = {
  message: PropTypes.object.isRequired,
  isMine: PropTypes.bool,
  seen: PropTypes.bool,
  onReply: PropTypes.func.isRequired,
  onReact: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  canModerate: PropTypes.bool,
};
